// src/pages/BusinessDashboard.jsx
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { businessService } from '../services/businessService';
import { calendarService } from '../services/calendarService';
import { proposalService } from '../services/proposalService';
import { useRealtimeSubscription } from '../hooks/useRealtimeSubscription';
import { supabase } from '../lib/supabase';
import toast from 'react-hot-toast';
import {
  Calendar,
  RefreshCw,
  Link as LinkIcon,
  Users,
  Mail,
  CheckCircle,
  XCircle,
  Search,
  Clock,
  X,
} from 'lucide-react';
import MatchCard from '../components/MatchCard';
import ProposalCard from '../components/ProposalCard';
import LoadingSpinner from '../components/LoadingSpinner';
import EmptyState from '../components/EmptyState';
import BusinessConsentList from '../components/BusinessConsentList';
import VennLinkManager from '../components/VennLinkManager';
import CalendarSelector from '../components/CalendarSelector';
import WebhookManager from '../components/WebhookManager';
import NetworkTab from '../components/NetworkTab';
import ErrorBoundary, { InlineErrorFallback } from '../components/ErrorBoundary';
const StripeConnection = lazy(() => import('../components/StripeConnection'));
import AppShell from '../components/AppShell';
import HomeView from '../components/HomeView';
import CalendarView from '../components/CalendarView';
import ChatButton from '../components/ai/ChatButton';
import ChatDrawer from '../components/ai/ChatDrawer';
import { FirstBookingBadge } from '../components/FirstBookingBadge';
import { useFirstBookingCelebration } from '../hooks/useFirstBookingCelebration';
import OnboardingModal from '../components/OnboardingModal';
import AppleConnectModal from '../components/AppleConnectModal';
import styles from './BusinessDashboard.module.css';

const API_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:3001';

export default function BusinessDashboard() {
  const { user, profile, signOut } = useAuth();
  const navigate = useNavigate();

  const [calendarConnections, setCalendarConnections] = useState([]);
  const [calendarConnection, setCalendarConnection] = useState(null);
  const [businessProfile, setBusinessProfile] = useState(null);
  const [stats, setStats] = useState(null);
  const [connections, setConnections] = useState([]);
  const [connectionsTruncated, setConnectionsTruncated] = useState(false);
  const [proposals, setProposals] = useState([]);
  const [matches, setMatches] = useState([]);
  const [vennLinks, setVennLinks] = useState([]);
  // SendProposalModal removed — match card sends inline directly. Keeping the
  // file on disk for reference / future multi-slot composer.

  const [usageData, setUsageData] = useState(null);

  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncingProviders, setSyncingProviders] = useState({});
  const [findingMatches, setFindingMatches] = useState(false);
  // TODO(naming) primary "activity" tab key — was 'offers'
  // 'analytics' tab removed — page hidden pending rewrite per separate audit.
  const VALID_TABS = new Set([
    'home', 'calendar', 'matches', 'network',
    'activity', 'venn-links', 'payments', 'webhooks',
    'permissions', 'profile'
  ]);
  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const activeTab = VALID_TABS.has(rawTab) ? rawTab : 'home';
  const setActiveTab = (tab) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      tab === 'home' ? next.delete('tab') : next.set('tab', tab);
      return next;
    }, { replace: true });
  };
  const [error, setError] = useState(null);
  const [showCalendarSelector, setShowCalendarSelector] = useState(false);
  const [appleModalOpen, setAppleModalOpen] = useState(false);
  const [calendarRefreshKey, setCalendarRefreshKey] = useState(0);
  const [expandedEmails, setExpandedEmails] = useState(new Set());
  const [chatOpen, setChatOpen] = useState(false);

  const totalBookingsCount = vennLinks.reduce((sum, l) => sum + (l.bookings_count ?? 0), 0);
  useFirstBookingCelebration(user?.id, totalBookingsCount, null);

  useEffect(() => {
    if (!user) return;
    const ctrl = { cancelled: false };
    loadDashboardData(ctrl);
    return () => { ctrl.cancelled = true; };
  }, [user?.id]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const microsoftConnected = params.get('microsoft_connected');
    const microsoftError = params.get('microsoft_error');
    const googleConnected = params.get('google_connected');
    const googleError = params.get('google_error');

    // Wave 4: legacy /dashboard?offer=<id> deep-links bounce to home with a toast.
    if (params.get('offer')) {
      // TODO(naming) legacy offer-link redirect message
      toast('Offers have been replaced — view your activity.');
      navigate('/', { replace: true });
      return;
    }

    const reloadConnections = () => {
      if (user) {
        calendarService.getCalendarConnections(user.id).then(connections => {
          setCalendarConnections(connections || []);
          setCalendarConnection(connections?.[0] || null);
        });
      }
    };

    const clearConnectionParams = () => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        for (const key of ['microsoft_connected', 'microsoft_error', 'google_connected', 'google_error']) {
          next.delete(key);
        }
        return next;
      }, { replace: true });
    };

    if (microsoftConnected === 'true') {
      toast.success('Microsoft 365 calendar connected successfully!');
      reloadConnections();
      clearConnectionParams();
    } else if (microsoftError) {
      toast.error(`Failed to connect Microsoft 365: ${microsoftError}`);
      clearConnectionParams();
    } else if (googleConnected === 'true') {
      toast.success('Google Calendar connected successfully!');
      reloadConnections();
      clearConnectionParams();
    } else if (googleError) {
      toast.error(`Failed to connect Google Calendar: ${googleError}`);
      clearConnectionParams();
    }
  }, [user?.id]);

  // Wave 4: see CustomerDashboard for the rationale on the unfiltered
  // proposals subscription — participant membership lives in a join table,
  // RLS gates visibility, and we re-fetch on any change.
  useRealtimeSubscription(`business-proposals-${user?.id}`, 'proposals', {
    enabled: !!user,
    onInsert: () => {
      if (user) proposalService.list().then(r => setProposals(r.data?.proposals || r.data || []));
    },
    onUpdate: () => {
      if (user) proposalService.list().then(r => setProposals(r.data?.proposals || r.data || []));
    },
    onDelete: () => {
      if (user) proposalService.list().then(r => setProposals(r.data?.proposals || r.data || []));
    }
  });

  // Refresh connections from /v1/network/connections. The two realtime
  // subscriptions below both call this — a single insert event fires on
  // exactly one channel (rows can't satisfy both filters), so no double-fetch.
  const refreshConnections = () => {
    if (!user) return;
    businessService.getNetworkConnections().then(result => {
      setConnections(result.data || []);
      setConnectionsTruncated(result.hasMore || false);
    });
  };

  useRealtimeSubscription(`business-conn-out-${user?.id}`, 'business_customer_connections', {
    filter: `business_id=eq.${user?.id}`,
    enabled: !!user,
    onInsert: () => {
      refreshConnections();
      setStats(prev => prev ? { ...prev, totalCustomers: (prev.totalCustomers || 0) + 1 } : null);
    },
    onUpdate: refreshConnections
  });

  // Flip-side subscription: B2B connections where this user is the customer_id.
  // Without this, B2B partners invited by other businesses never appear here.
  useRealtimeSubscription(`business-conn-in-${user?.id}`, 'business_customer_connections', {
    filter: `customer_id=eq.${user?.id}`,
    enabled: !!user,
    onInsert: refreshConnections,
    onUpdate: refreshConnections
  });

  const loadDashboardData = async (ctrl = { cancelled: false }) => {
    try {
      setLoading(true);
      setError(null);

      const { data: { session } } = await supabase.auth.getSession();

      const [
        calendarResult,
        profileResult,
        connectionsResult,
        proposalsResult,
        vennLinksResult,
        usageResult,
        schedulesResult
      ] = await Promise.all([
        calendarService.getCalendarConnections(user.id),
        businessService.getBusinessProfile(user.id),
        businessService.getNetworkConnections(),
        proposalService.list(),
        session
          ? fetch(`${API_URL}/v1/venn-links`, {
              headers: { 'Authorization': `Bearer ${session.access_token}` }
            }).then(r => r.ok ? r.json() : { venn_links: [] }).catch(() => ({ venn_links: [] }))
          : Promise.resolve({ venn_links: [] }),
        session
          ? fetch(`${API_URL}/v1/billing/usage`, {
              headers: { 'Authorization': `Bearer ${session.access_token}` }
            }).then(r => r.ok ? r.json() : null).catch(() => null)
          : Promise.resolve(null),
        supabase
          .from('schedules')
          .select('id')
          .eq('user_id', user.id)
          .limit(1)
      ]);

      if (ctrl.cancelled) return;

      const proposalList = proposalsResult.data?.proposals || proposalsResult.data || [];
      const statsResult = await businessService.getDashboardStats(user.id, proposalList);
      if (ctrl.cancelled) return;

      setCalendarConnections(calendarResult || []);
      setCalendarConnection(calendarResult?.[0] || null);
      setBusinessProfile(profileResult.data);
      setStats(statsResult.data);
      setConnections(connectionsResult.data || []);
      setConnectionsTruncated(connectionsResult.hasMore || false);
      setProposals(proposalList);
      setVennLinks(vennLinksResult.venn_links || []);
      setUsageData(usageResult);

      // Onboarding check: show modal if timezone is NULL or no schedules exist
      const hasSchedule = schedulesResult.data && schedulesResult.data.length > 0;
      setNeedsOnboarding(!profile?.timezone || !hasSchedule);
    } catch (err) {
      if (ctrl.cancelled) return;
      console.error('Error loading dashboard:', err);
      setError('Failed to load dashboard data');
    } finally {
      if (!ctrl.cancelled) setLoading(false);
    }
  };

  const handleConnectCalendar = async () => {
    window.location.href = await calendarService.getGoogleAuthUrl();
  };

  const handleConnectMicrosoft = async () => {
    window.location.href = await calendarService.getMicrosoftAuthUrl();
  };

  const handleConnectApple = () => setAppleModalOpen(true);

  // Apple connect is a direct POST (no OAuth redirect), so refresh in-place.
  const handleAppleConnected = async () => {
    const updatedConnections = await calendarService.getCalendarConnections(user.id);
    setCalendarConnections(updatedConnections || []);
    setCalendarConnection(updatedConnections?.[0] || null);
    setCalendarRefreshKey(prev => prev + 1);
  };

  const handleSyncNow = async () => {
    try {
      setSyncing(true);
      setError(null);
      if (!calendarConnection) throw new Error('No calendar connection found');

      const result = await calendarService.syncAvailability(user.id, calendarConnection);
      const updatedConnections = await calendarService.getCalendarConnections(user.id);
      setCalendarConnections(updatedConnections || []);
      setCalendarConnection(updatedConnections?.[0] || null);
      setCalendarRefreshKey(prev => prev + 1);

      const calendarText = result.calendarsCount === 1 ? 'calendar' : 'calendars';
      toast.success(`Successfully synced ${result.calendarsCount} ${calendarText} with ${result.blocksCount} busy blocks!`);
    } catch (err) {
      console.error('Error syncing calendar:', err);
      toast.error('Failed to sync calendar');
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncProvider = async (provider) => {
    try {
      setSyncingProviders(prev => ({ ...prev, [provider]: true }));
      setError(null);

      const connection = calendarConnections.find(c => c.provider === provider);
      if (!connection) throw new Error(`No ${provider} connection found`);

      const result = await calendarService.syncAvailability(user.id, connection);
      const updatedConnections = await calendarService.getCalendarConnections(user.id);
      setCalendarConnections(updatedConnections || []);
      setCalendarConnection(updatedConnections?.[0] || null);
      setCalendarRefreshKey(prev => prev + 1);

      const providerName = provider === 'google' ? 'Google' : 'Microsoft 365';
      const calendarText = result.calendarsCount === 1 ? 'calendar' : 'calendars';
      toast.success(`${providerName}: Synced ${result.calendarsCount} ${calendarText} with ${result.blocksCount} busy blocks!`);
    } catch (err) {
      console.error(`Error syncing ${provider}:`, err);
      toast.error(`Failed to sync ${provider === 'google' ? 'Google' : 'Microsoft 365'} calendar`);
    } finally {
      setSyncingProviders(prev => ({ ...prev, [provider]: false }));
    }
  };

  const handleSyncAllProviders = async () => {
    try {
      setSyncing(true);
      setError(null);
      if (calendarConnections.length === 0) throw new Error('No calendar connections found');

      const result = await calendarService.syncAllProviders(user.id);
      const updatedConnections = await calendarService.getCalendarConnections(user.id);
      setCalendarConnections(updatedConnections || []);
      setCalendarConnection(updatedConnections?.[0] || null);
      setCalendarRefreshKey(prev => prev + 1);

      if (result.success) {
        toast.success(`Synced ${result.successCount} provider(s) with ${result.totalBlocks} total busy blocks!`);
      } else {
        toast.error('Some providers failed to sync');
      }
    } catch (err) {
      console.error('Error syncing all providers:', err);
      toast.error('Failed to sync calendars');
    } finally {
      setSyncing(false);
    }
  };

  const handleDisconnectProvider = async (provider) => {
    const labels = { google: 'Google', microsoft: 'Microsoft 365', apple: 'Apple Calendar' };
    const providerName = labels[provider] || provider;
    if (!confirm(`Are you sure you want to disconnect ${providerName}?`)) return;

    try {
      await calendarService.disconnectCalendar(user.id, provider);
      const updatedConnections = await calendarService.getCalendarConnections(user.id);
      setCalendarConnections(updatedConnections || []);
      setCalendarConnection(updatedConnections?.[0] || null);
      setCalendarRefreshKey(prev => prev + 1);
      toast.success(`${providerName} disconnected`);
      // C4: the app-specific password stays live in the user's Apple ID until they remove it.
      if (provider === 'apple') {
        toast('Remember to delete the Vennio app-specific password in your Apple ID settings (appleid.apple.com).', { duration: 8000, icon: 'ℹ️' });
      }
    } catch (err) {
      console.error(`Error disconnecting ${provider}:`, err);
      toast.error(`Failed to disconnect ${providerName}`);
    }
  };

  const handleFindMatches = async () => {
    try {
      setFindingMatches(true);
      setError(null);

      if (!calendarConnection) {
        alert('Please connect your calendar first');
        return;
      }

      const { data, error: matchError } = await businessService.findAvailabilityMatches(user.id, { daysAhead: 14 });
      if (matchError) throw matchError;

      setMatches(data || []);
      setActiveTab('matches');

      if (!data || data.length === 0) {
        alert('No matches found. Make sure you have customers opted in and both calendars are synced.');
      }
    } catch (err) {
      console.error('Error finding matches:', err);
      setError('Failed to find matches');
    } finally {
      setFindingMatches(false);
    }
  };

  const refreshProposals = async () => {
    const { data } = await proposalService.list();
    const list = data?.proposals || data || [];
    setProposals(list);
    const statsResult = await businessService.getDashboardStats(user.id, list);
    setStats(statsResult.data);
  };

  // Wave 4 UX fix: MatchCard sends inline. The card already has the recipient,
  // a slot {start, end}, and an optional message. We just call the API.
  // API contract (api/schemas/proposals.js): slots use `start` / `end`, NOT
  // `start_time` / `end_time`. The previous modal got this wrong → 400.
  const handleSendProposalFromMatch = async ({ customerId, startTime, endTime, message }) => {
    if (!customerId || !startTime || !endTime) {
      // TODO(naming) inline send-proposal validation toast
      toast.error('Match data incomplete — refresh matches and try again.');
      return;
    }
    const { error: createError } = await proposalService.create({
      participantIds: [customerId],
      slots: [{ start: startTime, end: endTime }],
      message: message?.trim() || undefined,
    });
    if (createError) {
      // TODO(naming) inline send-proposal failure toast
      toast.error(createError.message || 'Could not send proposal.');
      throw createError;
    }
    // TODO(naming) inline send-proposal success toast
    toast.success('Proposal sent.');
    await refreshProposals();
  };

  const handleCancelProposal = async (proposalId) => {
    setError(null);
    const { error: cancelError } = await proposalService.cancel(proposalId);
    if (cancelError) {
      // TODO(naming) cancel failure toast
      toast.error('Could not cancel meeting.');
      return;
    }
    // TODO(naming) cancel success toast
    toast.success('Meeting cancelled.');
    refreshProposals();
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    try {
      setError(null);
      const formData = new FormData(e.target);
      const profileData = {
        business_name: formData.get('business_name'),
        service_type: formData.get('service_type'),
        location: formData.get('location'),
        address: formData.get('address'),
        default_appointment_duration: parseInt(formData.get('duration')) || 30,
        description: formData.get('description')
      };
      const { error: saveError } = await businessService.updateBusinessProfile(user.id, profileData);
      if (saveError) throw saveError;
      setBusinessProfile(profileData);
      alert('Profile saved successfully!');
    } catch (err) {
      console.error('Error saving profile:', err);
      setError('Failed to save profile');
    }
  };

  if (loading) {
    return (
      <div className={styles.loadingPage}>
        <LoadingSpinner size="lg" text="Loading dashboard..." />
      </div>
    );
  }

  const pendingProposals = proposals.filter(p => p.status === 'proposed');

  return (
    <AppShell
      businessName={businessProfile?.business_name || 'Vennio'}
      userEmail={profile?.email}
      userName={profile?.full_name || user?.user_metadata?.full_name || user?.user_metadata?.name || profile?.email?.split('@')[0]}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      onSignOut={() => signOut()}
      badges={{ matches: matches.length, activity: pendingProposals.length }}
    >
      {needsOnboarding && (
        <OnboardingModal onComplete={() => setNeedsOnboarding(false)} />
      )}

      {/* Usage warning banners */}
      {usageData && usageData.bookings.percent >= 100 && (
        <div className={styles.usageLimitBanner}>
          You've reached your monthly booking limit. New bookings are paused.{' '}
          <a href="/settings/billing" className={styles.usageBannerLink}>Upgrade now to resume.</a>
        </div>
      )}
      {usageData && usageData.bookings.percent >= 80 && usageData.bookings.percent < 100 && (
        <div className={styles.usageWarningBanner}>
          You've used {usageData.bookings.used} of your {usageData.bookings.limit} bookings this month.{' '}
          <a href="/settings/billing" className={styles.usageBannerLink}>Upgrade to keep accepting bookings.</a>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className={styles.errorBox}>
          <XCircle size={20} className={styles.errorIcon} />
          <div>
            <p className={styles.errorTitle}>Error</p>
            <p className={styles.errorText}>{error}</p>
          </div>
        </div>
      )}

      {activeTab === 'home' && totalBookingsCount > 0 && (
        <div style={{ paddingBottom: '8px' }}>
          <FirstBookingBadge userId={user.id} />
        </div>
      )}

      {activeTab === 'home' && (
        <HomeView
          stats={stats}
          calendarConnections={calendarConnections}
          calendarConnection={calendarConnection}
          calendarRefreshKey={calendarRefreshKey}
          syncingProviders={syncingProviders}
          syncing={syncing}
          userId={user.id}
          userName={profile?.full_name || user?.user_metadata?.full_name || user?.user_metadata?.name || profile?.email?.split('@')[0]}
          onSyncProvider={handleSyncProvider}
          onSyncAll={handleSyncAllProviders}
          onConnectGoogle={handleConnectCalendar}
          onConnectMicrosoft={handleConnectMicrosoft}
          onConnectApple={handleConnectApple}
          onDisconnectProvider={handleDisconnectProvider}
          onShowCalendarSelector={() => setShowCalendarSelector(true)}
          onFindMatches={handleFindMatches}
          findingMatches={findingMatches}
        />
      )}

      {/* 'analytics' tab render removed — page hidden pending rewrite. */}

      {activeTab === 'calendar' && calendarConnections.length > 0 && (
        <CalendarView userId={user.id} refreshTrigger={calendarRefreshKey} />
      )}

      {activeTab === 'calendar' && calendarConnections.length === 0 && (
        <EmptyState
          icon={Calendar}
          title="No calendar connected"
          description="Connect a calendar from the Home tab to view your availability."
        />
      )}

      {activeTab === 'matches' && (
        <div>
          {matches.length === 0 ? (
            <EmptyState
              icon={Search}
              title="No matches found"
              description="Click 'Find Matches' to discover time slots when you and your customers are both available."
              action={
                <button
                  onClick={handleFindMatches}
                  disabled={!calendarConnection}
                  className={styles.accentBtn}
                >
                  Find Matches Now
                </button>
              }
            />
          ) : (
            <div>
              <p className={styles.matchesExplainer}>
                Matches are people in your network who are available at the same time as you. Select a time slot and send them a booking offer.
              </p>
              <p className={styles.matchesSummary}>
                {(() => {
                  const people = new Set(matches.map(m => m.customer_email || m.customer_id));
                  return `${people.size} ${people.size === 1 ? 'person' : 'people'} · ${matches.length} available slot${matches.length !== 1 ? 's' : ''}`;
                })()}
              </p>
              {(() => {
                const grouped = new Map();
                matches.forEach(m => {
                  const key = m.customer_email || m.customer_id;
                  if (!grouped.has(key)) grouped.set(key, []);
                  grouped.get(key).push(m);
                });
                return Array.from(grouped.entries()).map(([key, slots]) => {
                  const isExpanded = expandedEmails.has(key);
                  const visibleSlots = isExpanded ? slots : slots.slice(0, 3);
                  const hiddenCount = slots.length - 3;

                  return (
                    <div key={key} className={styles.matchGroup}>
                      <div className={styles.matchGroupHeader}>
                        <h3 className={styles.matchGroupName}>{slots[0].customer_name}</h3>
                        <span className={styles.matchGroupMeta}>{slots[0].customer_email} · {slots.length} slot{slots.length !== 1 ? 's' : ''}</span>
                      </div>
                      <div className={styles.matchesGrid}>
                        {visibleSlots.map((match, idx) => (
                          <MatchCard
                            key={`${match.customer_id}-${match.start_time}-${idx}`}
                            match={match}
                            onSendOffer={handleSendProposalFromMatch}
                            compact
                          />
                        ))}
                      </div>
                      {hiddenCount > 0 && !isExpanded && (
                        <button
                          className={styles.showAllBtn}
                          onClick={() => setExpandedEmails(prev => new Set(prev).add(key))}
                        >
                          Show all {slots.length} slots &rarr;
                        </button>
                      )}
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </div>
      )}

      {activeTab === 'network' && (
        <ErrorBoundary fallback={(error) => (
          <InlineErrorFallback error={error} title="Error loading network" onReset={() => window.location.reload()} />
        )}>
          <NetworkTab userId={user?.id} vennLinks={vennLinks} connections={connections} connectionsTruncated={connectionsTruncated} />
        </ErrorBoundary>
      )}

      {activeTab === 'activity' && (
        <div>
          {proposals.length === 0 ? (
            <EmptyState
              icon={Mail}
              /* TODO(naming) business empty-state title */
              title="No activity yet"
              /* TODO(naming) business empty-state description */
              description="When you send a meeting proposal, it'll show up here."
            />
          ) : (
            <div className={styles.offerList}>
              {proposals.map(proposal => (
                <ProposalCard
                  key={proposal.id}
                  proposal={proposal}
                  viewerId={user.id}
                  onCancel={handleCancelProposal}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {activeTab === 'venn-links' && (
        <ErrorBoundary fallback={(error) => (
          <InlineErrorFallback error={error} title="Error loading Venn Links" onReset={() => window.location.reload()} />
        )}>
          <VennLinkManager userId={user?.id} />
        </ErrorBoundary>
      )}

      {activeTab === 'payments' && (
        <Suspense fallback={null}>
          <StripeConnection userId={user?.id} />
        </Suspense>
      )}

      {activeTab === 'webhooks' && (
        <ErrorBoundary fallback={(error) => (
          <InlineErrorFallback error={error} title="Error loading webhooks" onReset={() => window.location.reload()} />
        )}>
          <WebhookManager />
        </ErrorBoundary>
      )}

      {activeTab === 'permissions' && (
        <BusinessConsentList userId={user?.id} />
      )}

      {activeTab === 'profile' && (
        <form onSubmit={handleSaveProfile} className={styles.profileForm}>
          <div className={styles.formField}>
            <label className={styles.formLabel}>Business Name *</label>
            <input
              type="text"
              name="business_name"
              required
              defaultValue={businessProfile?.business_name || ''}
              className={styles.formInput}
              placeholder="Joe's Barber Shop"
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.formLabel}>Service Type *</label>
            <input
              type="text"
              name="service_type"
              required
              defaultValue={businessProfile?.service_type || ''}
              className={styles.formInput}
              placeholder="Haircut, Dental, Consulting, etc."
            />
          </div>

          <div className={styles.twoColGrid}>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Location</label>
              <input
                type="text"
                name="location"
                defaultValue={businessProfile?.location || ''}
                className={styles.formInput}
                placeholder="New York, NY"
              />
            </div>
            <div className={styles.formField}>
              <label className={styles.formLabel}>Default Duration (minutes)</label>
              <input
                type="number"
                name="duration"
                min="15"
                max="240"
                step="15"
                defaultValue={businessProfile?.default_appointment_duration || 30}
                className={styles.formInput}
              />
            </div>
          </div>

          <div className={styles.formField}>
            <label className={styles.formLabel}>Address</label>
            <input
              type="text"
              name="address"
              defaultValue={businessProfile?.address || ''}
              className={styles.formInput}
              placeholder="123 Main St, Suite 100"
            />
          </div>

          <div className={styles.formField}>
            <label className={styles.formLabel}>Description</label>
            <textarea
              name="description"
              rows={4}
              defaultValue={businessProfile?.description || ''}
              className={styles.formTextarea}
              placeholder="Tell customers about your business..."
            />
          </div>

          <button type="submit" className={`${styles.accentBtn} ${styles.accentBtnFull}`}>
            Save Profile
          </button>
        </form>
      )}

      {/* SendProposalModal removed — MatchCard sends inline. Files preserved
          on disk for the future multi-slot composer. */}

      {/* Calendar Selector Modal */}
      {showCalendarSelector && (
        <CalendarSelector
          onClose={() => setShowCalendarSelector(false)}
          onSave={(selectedIds) => {
            console.log('Calendars selected:', selectedIds);
            handleSyncNow();
          }}
        />
      )}
      <AppleConnectModal
        isOpen={appleModalOpen}
        onClose={() => setAppleModalOpen(false)}
        onConnected={handleAppleConnected}
      />
      <ChatButton onClick={() => setChatOpen(true)} />
      <ChatDrawer
        open={chatOpen}
        onClose={() => setChatOpen(false)}
        activeTab={activeTab}
        calendarConnected={!!calendarConnection}
        hasConnections={connections.length > 0}
        hasMatches={matches.length > 0}
      />
    </AppShell>
  );
}
